import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { CoreApplicationContext } from '@eventbrite/legacy-infra.context-types';
import { ConsumerHeaderSearchBar } from '@eventbrite/consumer-header.global-nav';
import { getLogoInfo, getUserMenuInfo } from '@eventbrite/legacy-infra.site-header';
import classNames from 'classnames';
import { IconButton, MagnifyingGlassIcon } from '@eventbrite/marmalade';
import type {
    FeatureFlags,
    RequestShape,
    searchBarProps,
    UserShape,
} from '@eventbrite/consumer-header.global-nav/types';
import { GlobalHeaderLogo } from '../GlobalHeaderLogo';
import { GlobalHeaderMenus } from '../GlobalHeaderNavigationBar';
import {
    ConsumerHeaderDesktopLinks,
    ConsumerHeaderMobileLinks,
} from '../ConsumerHeaderLinks/ConsumerHeaderLinks';

import styles from './ConsumerHeader.module.scss';

const CONSUMER_HEADER_COLLAPSED_SEARCH_MAX_PX = 660;

export interface ConsumerHeaderProps {
    env: CoreApplicationContext['env'] & {
        launchNewSignInSignUpLinks?: boolean;
        isManageOverviewActive?: boolean;
        localeInfo?: Partial<CoreApplicationContext['env']['localeInfo']>;
    };
    request: RequestShape;
    user: UserShape;
    onAdditionalMenuTextItemSelect?: Function;
    isHomePage?: boolean;
    id?: string;
    featureFlags: FeatureFlags;
    hasBreadcrumbs?: boolean;
    searchBar?: searchBarProps;
}
export const ConsumerHeader: React.FunctionComponent<ConsumerHeaderProps> = (props) => {
    const {
        user,
        env,
        id,
        featureFlags,
        hasBreadcrumbs,
        request,
        searchBar,
        isHomePage,
        onAdditionalMenuTextItemSelect,
    } = props;

    const { serverUrl, adminServerUrl, logoutUrl, helpUrls } = env;
    const { path: requestPath } = request;
    const serverUrlLanguageSubdirectoy = env?.localeInfo?.uses_language_subdirectory
        ? `${serverUrl}/${env.localeInfo.locale}`
        : `${serverUrl}`;
    const { formatMessage } = useIntl();

    const userMenuInfo = getUserMenuInfo({
        adminServerUrl,
        featureFlags: {
            ...featureFlags,
            isDetachInterestsEnabled: false,
        },
        logoutUrl,
        requestPath,
        serverUrl,
        showBrowseEvents: true,
        user,
        helpUrls,
        showHelpMenu: featureFlags?.showHelpCenterMenu ?? false,
    });

    const [isSearchExpanded, setSearchExpanded] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }
        const query = `(min-width: ${CONSUMER_HEADER_COLLAPSED_SEARCH_MAX_PX + 1}px)`;
        const mql = window.matchMedia(query);
        const onViewportChange = () => {
            if (mql.matches) {
                setSearchExpanded(false);
            }
        };
        onViewportChange();
        mql.addEventListener('change', onViewportChange);
        return () => mql.removeEventListener('change', onViewportChange);
    }, []);

    const headerClassnames = classNames(styles.root, styles.searchDropdown, {
        [styles.breadcrumbs as string]: hasBreadcrumbs,
        [styles.mobileSearchExpanded as string]: isSearchExpanded,
    });

    const renderSearchArea = () => {
        if (isSearchExpanded) {
            return null;
        }
        return (
            <div className={styles.inlineSearchWhenWide}>
                <ConsumerHeaderSearchBar user={user} env={env} searchBar={searchBar} />
            </div>
        );
    };

    const searchTriggerButton = (
        <IconButton
            icon={<MagnifyingGlassIcon size="small" />}
            aria-label={
                isSearchExpanded
                    ? formatMessage({
                          id: 'Close search',
                          defaultMessage: 'Close search',
                      })
                    : formatMessage({
                          id: 'Open search',
                          defaultMessage: 'Open search',
                      })
            }
            onClick={() => setSearchExpanded(!isSearchExpanded)}
            className={styles.searchTriggerButton}
            data-testid="consumer-header-search-trigger"
        />
    );

    return (
        <header
            id={id || 'global-header'}
            className={headerClassnames}
            data-testid="consumer-header"
        >
            <a className={styles.skipLinks} href="#skip-heading">
                {formatMessage({
                    id: 'Skip Main Navigation',
                    defaultMessage: 'Skip Main Navigation',
                })}
            </a>
            <div className={styles.headerContainer} data-role="header">
                <div className={styles.headerContent}>
                    <GlobalHeaderLogo
                        info={getLogoInfo(serverUrlLanguageSubdirectoy, requestPath, {
                            iconWidth: '110px',
                        })}
                    />
                    {renderSearchArea()}
                    <div
                        className={styles.desktopLinksContainer}
                        data-testid="consumer-header-desktop"
                    >
                        <ConsumerHeaderDesktopLinks
                            user={user}
                            env={env}
                            featureFlags={featureFlags}
                            requestPath={requestPath}
                            isHomePage={isHomePage}
                            onAdditionalMenuTextItemSelect={onAdditionalMenuTextItemSelect}
                        />
                        <GlobalHeaderMenus userMenuInfo={userMenuInfo} />
                    </div>
                    <div className={styles.rightGroup}>
                        <div className={styles.collapsedSearchOnly}>{searchTriggerButton}</div>
                        <div
                            className={styles.mobileLinksContainer}
                            data-testid="consumer-header-mobile"
                        >
                            <ConsumerHeaderMobileLinks
                                user={user}
                                env={env}
                                isHomePage={isHomePage}
                                featureFlags={featureFlags}
                                requestPath={requestPath}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.headerContent}>
                    {isSearchExpanded && (
                        <div className={styles.searchExpandedNarrowOnly}>
                            <div
                                className={styles.searchExpandedBelow}
                                data-testid="consumer-header-search-expanded"
                            >
                                <ConsumerHeaderSearchBar
                                    user={user}
                                    env={env}
                                    searchBar={searchBar}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <span id="skip-heading" className={styles.skipHeading}>
                {formatMessage({ id: 'Page Content', defaultMessage: 'Page Content' })}
            </span>
        </header>
    );
};
